/**
 * Converts Quill 2.x internal HTML to standard HTML.
 *
 * Quill 2.x stores all lists as flat <ol> with data-list attributes and
 * ql-indent-N classes for nesting. This function converts that to proper
 * nested <ul>/<ol> HTML that renders correctly outside the Quill editor.
 *
 * Adds inline styles to guarantee rendering regardless of CSS context.
 */

// Quill's ordered list nesting pattern: decimal > lower-alpha > lower-roman (repeats)
const OL_STYLES = ['decimal', 'lower-alpha', 'lower-roman'];
const UL_STYLES = ['disc', 'circle', 'square'];

export function sanitizeQuillHtml(html) {
  if (!html) return html;

  const container = document.createElement('div');
  container.innerHTML = html;

  // Remove Quill UI marker spans
  container.querySelectorAll('span.ql-ui').forEach(el => el.remove());

  // Process all <ol> elements that contain Quill data-list items
  const olElements = Array.from(container.querySelectorAll('ol'));
  olElements.forEach(ol => {
    const items = Array.from(ol.querySelectorAll(':scope > li'));
    if (items.length === 0) return;

    // Check if this is a Quill-formatted list (has data-list attributes)
    const hasDataList = items.some(li => li.hasAttribute('data-list'));
    if (!hasDataList) return;

    // Build nested list structure from flat Quill format
    const fragment = buildNestedList(items);
    ol.parentNode.replaceChild(fragment, ol);
  });

  // Apply inline styles to all lists for consistent rendering
  applyListStyles(container, 0);

  return container.innerHTML;
}

/**
 * Recursively applies inline styles to list elements to ensure
 * proper rendering in any CSS context.
 */
function applyListStyles(root, depth) {
  const lists = root.querySelectorAll(':scope > ul, :scope > ol');
  lists.forEach(list => {
    const tag = list.tagName.toLowerCase();
    const styles = tag === 'ol' ? OL_STYLES : UL_STYLES;
    const styleType = styles[depth % styles.length];

    list.style.listStyleType = styleType;
    list.style.paddingLeft = '1.5em';
    list.style.marginTop = '0';
    list.style.marginBottom = '0';

    // Recurse into child <li> elements to find nested lists
    Array.from(list.children).forEach(li => {
      if (li.tagName.toLowerCase() === 'li') {
        applyListStyles(li, depth + 1);
      }
    });
  });
}

/**
 * Gets the indent level from a Quill list item's class.
 */
function getIndentLevel(li) {
  const classes = li.className || '';
  const match = classes.match(/ql-indent-(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Gets the list tag for a Quill list item.
 */
function getListTag(li) {
  const type = li.getAttribute('data-list') || 'ordered';
  if (type === 'bullet') return 'ul';
  if (type === 'ordered') return 'ol';
  if (type === 'checked' || type === 'unchecked') return 'ul';
  return 'ol';
}

/**
 * Cleans Quill-specific attributes/classes from a list item
 * and returns a fresh <li> with the original inner content.
 */
function cleanListItem(li) {
  const newLi = document.createElement('li');
  const dataList = li.getAttribute('data-list');

  // Handle checked/unchecked as checkbox
  if (dataList === 'checked' || dataList === 'unchecked') {
    newLi.className = 'checklist-item';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'checklist-cb';
    checkbox.disabled = true;
    checkbox.checked = dataList === 'checked';
    checkbox.style.marginRight = '6px';
    newLi.appendChild(checkbox);
  }

  newLi.innerHTML += li.innerHTML;
  return newLi;
}

/**
 * Builds proper nested list DOM from Quill's flat list items.
 *
 * Returns a DocumentFragment that may contain multiple root-level
 * list elements (when list type changes at indent 0).
 */
function buildNestedList(items) {
  const parsed = items.map(li => ({
    indent: getIndentLevel(li),
    tag: getListTag(li),
    li: cleanListItem(li)
  }));

  const fragment = document.createDocumentFragment();

  // Stack entries: { listEl, indent, tag }
  let stack = [];

  for (let i = 0; i < parsed.length; i++) {
    const { indent, tag, li } = parsed[i];

    if (stack.length === 0) {
      // First item — create root list
      const list = document.createElement(tag);
      list.appendChild(li);
      fragment.appendChild(list);
      stack = [{ listEl: list, indent: 0, tag }];
      continue;
    }

    // Pop stack back to the level at or below this item's indent
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    const parent = stack[stack.length - 1];

    if (indent === 0 && tag !== parent.tag) {
      // Root level type change → start a new root list
      const list = document.createElement(tag);
      list.appendChild(li);
      fragment.appendChild(list);
      stack = [{ listEl: list, indent: 0, tag }];
    } else if (indent <= parent.indent && tag === parent.tag) {
      // Same level, same type → sibling
      parent.listEl.appendChild(li);
    } else if (indent <= parent.indent && tag !== parent.tag) {
      // Same non-root level, different type → new list inside the parent's last <li>
      const grandparent = stack.length > 1 ? stack[stack.length - 2] : stack[0];
      const targetLi = grandparent.listEl.lastElementChild;
      const newList = document.createElement(tag);
      newList.appendChild(li);
      if (targetLi) {
        targetLi.appendChild(newList);
      }
      stack[stack.length - 1] = { listEl: newList, indent, tag };
    } else {
      // Deeper indent → nest inside the last <li> of current list
      let targetLi = parent.listEl.lastElementChild;
      if (!targetLi) {
        targetLi = document.createElement('li');
        parent.listEl.appendChild(targetLi);
      }
      const newList = document.createElement(tag);
      newList.appendChild(li);
      targetLi.appendChild(newList);
      stack.push({ listEl: newList, indent, tag });
    }
  }

  return fragment;
}
